import React, { useState } from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  SupervisorAccountOutlined,
  ShieldOutlined,
  ManageAccountsOutlined,
  AccountCircleOutlined,
} from '@mui/icons-material';

import ConfirmBar from '../../atoms/ConfirmBar/ConfirmBar';
import SupervisionModal from '../../molecules/Supervisionmodal/Supervisionmodal';
import ShareHierarchyModal from '../../molecules/ShareHierarchyModal/ShareHierarchyModal';
import AccessRightsModal from '../../molecules/AccessRightsModal/AccessRightsModal';
import SupervisorEditModal from '../../molecules/SupervisorEditModal/SupervisorEditModal';
import './UsersTable.css';

// ── Colonnes réutilisées à l'identique par le tableau d'en-tête et
// le tableau du corps (voir plus bas dans le rendu) : comme les deux
// <table> partagent EXACTEMENT le même <colgroup>, le navigateur
// calcule des largeurs de colonnes rigoureusement identiques pour
// les deux — un désalignement devient structurellement impossible,
// quel que soit l'écran ou le navigateur. ───────────────────────────
const TableColgroup = () => (
  <colgroup>
    <col className="col-user" />
    <col className="col-role" />
    <col className="col-supervisors" />
    <col className="col-org" />
    <col className="col-status" />
    <col className="col-actions" />
  </colgroup>
);


const UsersTable = ({
  usersList,
  allUsers = [],
  adminsList = [],
  isLoading,
  onEdit,
  onDelete,
  onToggleStatus,
  onSupervisionAdd,
  onSupervisionRemove,
  onSupervisionReplace,
  onSupervisionReplaceAll,
  onShareHierarchy,
  onSaveRights,
  onRevokeRights,
  currentUser,
  onOpenOwnProfile,
}) => {
  const [confirmAction, setConfirmAction]       = useState(null);
  const [supervisionAdmin, setSupervisionAdmin] = useState(null);
  const [shareAdmin, setShareAdmin]             = useState(null);
  const [accessRightsUser, setAccessRightsUser] = useState(null);
  const [supervisorEditUser, setSupervisorEditUser] = useState(null);
  const [togglingIds, setTogglingIds]           = useState(new Set());

  if (isLoading) return <div className="loading">Chargement...</div>;

  const handleDeleteClick = (user) => {
    setConfirmAction({
      type:      'delete',
      user,
      message:   'Supprimer',
      highlight: `${user.name} ${user.lastname}`,
    });
  };

  const handleStatusToggleClick = (user) => {
    setConfirmAction({
      type:      'status',
      user,
      message:   user.active ? 'Désactiver' : 'Activer',
      highlight: `${user.name} ${user.lastname}`,
    });
  };

  const handleConfirm = async () => {
    const { type, user } = confirmAction;

    if (type === 'delete') {
      await onDelete(user.id, user.username);
      setConfirmAction(null);
      return;
    }

    if (type === 'status') {
      setTogglingIds((prev) => new Set(prev).add(user.id));
      try {
        await onToggleStatus?.(user.id, !user.active);
      } catch {
        // le toast d'erreur est déjà géré côté hook (useUsers)
      } finally {
        setTogglingIds((prev) => {
          const next = new Set(prev);
          next.delete(user.id);
          return next;
        });
      }
    }

    setConfirmAction(null);
  };

  // ── Superviseurs : un nom complet par ligne, jamais tronqué ────
  // (plus de virgule de séparation ni d'ellipse : chaque superviseur
  // occupe sa propre ligne dans la cellule, voir .supervisor-cell
  // en column dans UsersTable.css)
  const renderSupervisors = (user) => {
    const content = (() => {
      if (user.supervisors && user.supervisors.length > 0) {
        return (
          <div className="supervisor-cell">
            {user.supervisors.map((supervisor) => (
              <span key={supervisor.id} className="supervisor-name">
                {supervisor.name} {supervisor.lastname || supervisor.username}
              </span>
            ))}
          </div>
        );
      }

      if (user.supervisor_name) {
        return (
          <div className="supervisor-cell">
            <span className="supervisor-name">{user.supervisor_name}</span>
          </div>
        );
      }

      return <span className="no-supervisor"> _________</span>;
    })();

    return (
      <div className="supervisor-cell-wrapper">
        {content}
        <button
          type="button"
          className="supervisor-edit-trigger"
          onClick={() => setSupervisorEditUser(user)}
          title="Modifier le(s) superviseur(s)"
        >
          <ManageAccountsOutlined />
        </button>
      </div>
    );
  };

  return (
    <>
      <div className="users-table-container">
        {/* ── Scroll horizontal UNIQUE, partagé par le header et le corps ──
            Les deux tables sont enfants directs de .users-table-inner :
            en scrollant horizontalement ici, header et corps se déplacent
            forcément ensemble, comme un seul bloc. ───────────────────── */}
        <div className="users-table-scroll-x">
          <div className="users-table-inner">

            {/* ── Table d'en-tête : fixe verticalement (hors de la zone
                de scroll vertical ci-dessous), donc toujours visible sans
                le moindre besoin de position:sticky — et donc sans les
                bugs de rendu que sticky provoque dans certains
                navigateurs sur des tableaux à double scroll. ────────── */}
            <table className="users-table users-table--header">
              <TableColgroup />
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Rôle</th>
                  <th>Superviseur(s)</th>
                  <th>Organisation</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
            </table>

            {/* ── Scroll vertical isolé, uniquement pour les lignes ──── */}
            <div className="users-table-scroll-y">
              <table className="users-table users-table--body">
                <TableColgroup />
                <tbody>
                  {usersList.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="no-data">Aucun utilisateur trouvé</td>
                    </tr>
                  ) : (
                    usersList.map((user) => {
                      const isAdmin       = user.role === 'admin';
                      const toggling      = togglingIds.has(user.id);
                      const isCurrentUser = !!user.is_current_user;
                      return (
                        <tr key={user.id} className={isCurrentUser ? 'current-user-row' : undefined}>

                          {/* ── Utilisateur ─────────────────────────────────── */}
                          <td>
                            <div className="user-cell">
                              <div className="user-avatar-wrapper">
                                <div className="user-avatar">
                                  {user.name?.[0]?.toUpperCase()}
                                  {user.lastname?.[0]?.toUpperCase()}
                                </div>
                                <span
                                  className={`online-dot ${user.is_online ? 'online' : 'offline'}`}
                                  title={user.is_online ? 'En ligne' : 'Hors ligne'}
                                />
                              </div>
                              <div className="user-info">
                                <div className="user-fullname-row">
                                  <span className="user-fullname">
                                    {user.name} {user.lastname}
                                  </span>
                                </div>
                                <span className="user-email">{user.email}</span>
                              </div>
                            </div>
                          </td>

                          {/* ── Rôle ────────────────────────────────────────── */}
                          <td>
                            <span className={`role-badge ${user.role}`}>{user.role}</span>
                          </td>

                          {/* ── Superviseurs ─────────────────────────────────── */}
                          <td>
                            {isCurrentUser
                              ? <span className="empty-dash">—</span>
                              : renderSupervisors(user)}
                          </td>

                          {/* ── Organisation ─────────────────────────────────── */}
                          <td className="user-muted">{user.organization || '—'}</td>

                          {/* ── Statut ───────────────────────────────────────── */}
                          <td>
                            {isCurrentUser ? (
                              <span className="empty-dash">—</span>
                            ) : (
                              <button
                                type="button"
                                className={[
                                  'user-status-toggle',
                                  user.active ? 'user-status-toggle--on' : 'user-status-toggle--off',
                                  toggling ? 'user-status-toggle--pending' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleStatusToggleClick(user)}
                                disabled={toggling}
                                title={user.active ? 'Actif — cliquer pour désactiver' : 'Inactif — cliquer pour activer'}
                              >
                                <span className="user-status-toggle-track">
                                  <span className="user-status-toggle-thumb">
                                    {toggling && <span className="user-status-toggle-spinner" />}
                                  </span>
                                </span>
                                <span className="user-status-toggle-label">
                                  {user.active ? 'Actif' : 'Inactif'}
                                </span>
                              </button>
                            )}
                          </td>

                          {/* ── Actions ─────────────────────────────────────── */}
                          <td>
                            {isCurrentUser ? (
                              <button
                                type="button"
                                className="my-profile-button"
                                onClick={() => onOpenOwnProfile?.(user)}
                              >
                                <AccountCircleOutlined />
                                <span className="my-profile-button__label">Voir mon profil</span>
                              </button>
                            ) : (
                              <div className="actions">
                                <button
                                  className="action-button access-rights"
                                  onClick={() => setAccessRightsUser(user)}
                                  title="Droits d'accès"
                                >
                                  <ShieldOutlined />
                                </button>

                                <button
                                  className={`action-button supervision${isAdmin ? '' : ' supervision--disabled'}`}
                                  onClick={() => isAdmin && setSupervisionAdmin(user)}
                                  title={isAdmin ? 'Modifier le(s) supervisé(s)' : undefined}
                                >
                                  <SupervisorAccountOutlined />
                                </button>

                                <button className="action-button edit" onClick={() => onEdit(user)} title="Modifier">
                                  <EditOutlined />
                                </button>

                                <button className="action-button delete" onClick={() => handleDeleteClick(user)} title="Supprimer">
                                  <DeleteOutlined />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}

      {confirmAction && (
        <ConfirmBar
          message={confirmAction.message}
          highlight={confirmAction.highlight}
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {supervisionAdmin && (
        <SupervisionModal
          admin={supervisionAdmin}
          allUsers={allUsers}
          onClose={() => setSupervisionAdmin(null)}
          onAdd={(targetAdminId) => onSupervisionAdd?.(supervisionAdmin.id, targetAdminId)}
          onRemove={(targetAdminId) => onSupervisionRemove?.(supervisionAdmin.id, targetAdminId)}
        />
      )}

      {shareAdmin && (
        <ShareHierarchyModal
          sourceAdmin={shareAdmin}
          onClose={() => setShareAdmin(null)}
          onSubmit={async (sourceAdminId, data) => {
            await onShareHierarchy?.(sourceAdminId, data);
            setShareAdmin(null);
          }}
        />
      )}

      {accessRightsUser && (
        <AccessRightsModal
          user={accessRightsUser}
          currentUser={currentUser}
          onClose={() => setAccessRightsUser(null)}
          onSave={onSaveRights}
          onRevoke={onRevokeRights}
        />
      )}

      {supervisorEditUser && (
        <SupervisorEditModal
          user={supervisorEditUser}
          adminsList={adminsList}
          onAdd={onSupervisionAdd}
          onRemove={onSupervisionRemove}
          onReplace={onSupervisionReplace}
          onReplaceAll={onSupervisionReplaceAll}
          onClose={() => setSupervisorEditUser(null)}
        />
      )}
    </>
  );
};

export default UsersTable;